import { t } from '@moovly/i18n';
import {
  ContentLoader,
  IconCalendar,
  IconCheck,
  IconClock,
  IconClose,
  IconMail,
  IconReceipt,
  IconTrash,
  IconUser,
} from '@moovly/ui';
import * as React from 'react';
import { formatDate } from '../../../../utils/date';
import { isActiveLicense } from '../../../../utils/license';
import { UserData } from '../../types/user';
import { LicenseData } from '../../../subscriptions/types/licenses';
import './UserInfo.scss';
import { Link } from 'react-router-dom';

const InfoSlice = ({
  Icon,
  text,
  children,
  loading = false,
}: {
  Icon: React.FC;
  text?: string;
  loading?: boolean;
  children?: React.ReactNode;
}) => (
  <div className="d-flex align-items-center  pb-2">
    <Icon />
    {(!loading || text) && <span className="ps-2">{text || (!children && '-')}</span>}
    {children && !loading && <div className="w-50 ps-2">{children}</div>}
    {loading && !text && <ContentLoader />}
  </div>
);

interface UserInfoProps {
  user?: UserData;
  license?: LicenseData | null;
  loading?: boolean;
  roleSelect: any;
  showAddSubscription?: boolean;
}

const UserInfo: React.FC<UserInfoProps> = ({ user, license, loading, roleSelect, showAddSubscription }) => {
  const lastLoginAt = React.useMemo(() => {
    if (!!user?.last_login_at)
      return t('userProfile.lastLogin', {
        date: formatDate(user?.last_login_at, 'yyyy-MM-LL HH:MM'),
      });
    return null;
  }, [user]);
  const { hasActiveLicense, licenseStatus, assignedContract, assignedLicenseSince, licenseExpiresAt } =
    React.useMemo(() => {
      const hasActiveLicense = !!license?.expires_at ? isActiveLicense(license) : false;
      const reference = license && license.contract.reference ? ` (${license.contract.reference})` : '';
      return {
        hasActiveLicense,
        licenseStatus: t(
          hasActiveLicense ? 'userProfile.userWithActiveLicense' : 'userProfile.userWithoutActiveLicense'
        ),
        assignedContract:
          license &&
          t('userProfile.assignedContract', {
            contract: `${t(
              `licenses.${license.contract.license_type}`,
              {},
              license.contract.license_type
            )} ${reference}`,
          }),
        assignedLicenseSince:
          license &&
          license.starts_at &&
          t('userProfile.assignedLicenseSince', {
            date: formatDate(license.starts_at),
          }),
        licenseExpiresAt:
          license &&
          license.expires_at &&
          t('userProfile.licenseExpiresAt', {
            date: formatDate(license.expires_at),
          }),
      };
    }, [license]);

  return (
    <div className="UserInfo">
      <h3>
        {user?.first_name} {user?.name}
      </h3>
      <div className="row">
        <div className="col-md-6">
          <InfoSlice loading={loading} Icon={IconMail} text={user?.email} />
          <InfoSlice loading={loading} Icon={IconClock} text={lastLoginAt} />
          {roleSelect && (
            <InfoSlice loading={loading} Icon={IconUser}>
              {roleSelect}
            </InfoSlice>
          )}
        </div>
        <div className="col-md-6">
          <InfoSlice loading={loading} Icon={hasActiveLicense ? IconCheck : IconClose} text={licenseStatus} />
          {hasActiveLicense || !showAddSubscription || loading ? (
            <>
              <InfoSlice loading={loading} Icon={IconReceipt} text={assignedContract} />
              <InfoSlice loading={loading} Icon={IconCalendar} text={assignedLicenseSince} />
              <InfoSlice loading={loading} Icon={IconTrash} text={licenseExpiresAt} />
            </>
          ) : (
            <Link to={`/users/${user?.id}/subscription?autoRevoke=true`} className="btn btn-secondary">
              Add Subscription
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserInfo;
